import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, ChevronRight, Globe, LogOut } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@wedding/shared";
import { useAuth } from "../contexts/auth-context";
import { cn } from "../lib/utils";

export function GuestUserMenu() {
  const { t, i18n } = useTranslation();
  const { guest, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"main" | "language">("main");
  const ref = useRef<HTMLDivElement>(null);

  const initial = (guest?.name ?? "?")[0]?.toUpperCase() ?? "?";
  const displayName = guest?.name ?? "";
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!isOpen) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setView("main");
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handle(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (view === "language") setView("main");
        else setIsOpen(false);
      }
    }
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [isOpen, view]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setIsOpen((v) => !v); setView("main"); }}
        aria-label={t("nav.profile")}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="h-8 w-8 shrink-0 rounded-full bg-primary/20 text-primary text-sm font-bold flex items-center justify-center hover:bg-primary/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {initial}
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 min-w-56 rounded-2xl border border-primary/10 bg-background/95 backdrop-blur-xl shadow-lg z-[9999] overflow-hidden"
        >
          {view === "main" ? (
            <>
              <div className="px-4 py-3 border-b border-primary/10">
                <p className="text-sm font-semibold truncate">{displayName}</p>
              </div>

              <div className="p-1.5 space-y-0.5">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setView("language")}
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-left">{currentLang.label}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              </div>

              <div className="border-t border-primary/10 p-1.5">
                <button
                  onClick={() => { setIsOpen(false); void logout(); }}
                  role="menuitem"
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  {t("common.logout")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-3 border-b border-primary/10">
                <button
                  type="button"
                  onClick={() => setView("main")}
                  className="p-1 rounded-md hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium">{t("common.language")}</span>
              </div>
              <div className="p-1.5 space-y-0.5">
                {SUPPORTED_LANGUAGES.map((lang) => {
                  const active = lang.code === i18n.language;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      role="menuitem"
                      onClick={() => { void i18n.changeLanguage(lang.code); setView("main"); }}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer",
                        active
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-foreground hover:bg-primary/5",
                      )}
                    >
                      <Check className={cn("h-3.5 w-3.5 shrink-0", active ? "opacity-100" : "opacity-0")} />
                      {lang.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
